import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { productWikiServices } from 'services/productWikiServices';
import { DataSource } from 'Interfaces/ProductWikiInterfaces/dataSources.types';
import {
  ANDAData,
  ANDAApplication,
  PaginationMetadata,
} from 'Interfaces/ProductWikiInterfaces/competitors.types';
import { toast } from 'react-toastify';

// Data interfaces
export interface OpportunityTile {
  value: string;
  label: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
}

export interface SparklineData {
  salesTrend: number[];
  volumeTrend: number[];
  salesChange: string;
  volumeChange: string;
}

export interface LOEStatus {
  status: string;
  description: string;
  badgeType: 'post-loe' | 'upcoming' | 'pre-loe';
}

export interface MarketMetrics {
  hhiScore: number;
  activeLabelers: number;
  newEntrants: number;
}

export interface MarketStructure {
  marketShare: Array<{
    name: string;
    percentage: number;
    color: string;
  }>;
  demandDynamics: {
    pricePoints: number[];
    volumePoints: number[];
    currentPrice?: number;
    currentVolume?: number;
    elasticity: number | string;
    seasonality: {
      q1: number;
      q2: number;
      q3: number;
      q4: number;
    };
  };
  metrics: MarketMetrics;
}

export interface NarrativeItem {
  title: string;
  items: string[];
}

export interface PriceData {
  currentBand: {
    median: number;
    q1: number;
    q3: number;
    spread: number;
  };
  trend: Array<{
    period: string;
    teva: number;
    mylan: number;
    amneal: number;
    sandoz: number;
    aurobindo: number;
    average: number;
  }>;
}

export interface PriceEngine {
  suggestedWac: string;
  expectedGovNet: string;
  grossMargin: string;
  inputs: {
    targetMargin: number;
    cogsEstimate: number;
    discountRate: number;
  };
  scenarios: Array<{
    type: string;
    wac: string;
    govNet: string;
    margin: string;
    description: string;
  }>;
}

export interface CompetitorData {
  manufacturers: Array<{
    name: string;
    labeler: string;
    ndcCount: number;
    ranking: number;
    averageWac: number | null;
    entryDate: string;
  }>;
  barriers: Array<{
    metric: string;
    value: string;
    label: string;
  }>;
}

export interface RegulatoryData {
  countryMatrix: Array<{
    id: number;
    manufacturer: string;
    apiSource: string;
    fdfManufacturing: string;
    taaStatus: boolean;
    volumeShare: string;
    fss: string;
    dapa: string;
    riskLevel: 'low' | 'medium' | 'high';
  }>;
  taaSummary: {
    taaPercentage: number;
    taaCountry: string;
    nonTaaPercentage: number;
    nonTaaCountry: string;
    summaryText: string;
  };
  attributes: Array<{
    title: string;
    items: Array<{
      label: string;
      value: string;
    }>;
  }>;
  complianceScore: {
    score: number;
    level: 'high' | 'moderate' | 'low';
    description: string;
    factors: Array<{
      name: string;
      weight: string;
      score: string;
      details: string;
    }>;
  };
}

export interface BreadcrumbData {
  items: Array<{
    label: string;
    href?: string;
    isActive?: boolean;
  }>;
}

export interface ProductAnalysisData {
  breadcrumb: BreadcrumbData;
  overview: {
    tiles: OpportunityTile[];
    loeStatus: LOEStatus;
  };
  sparklines: SparklineData;
  marketLandscape: MarketStructure;
  narrative: NarrativeItem[];
  pricing: PriceData;
  priceEngine: PriceEngine;
  competitors: CompetitorData;
  anda: ANDAData;
  regulatory: RegulatoryData;
  compliance: Record<string, unknown>; // Additional compliance data
  dataSources: DataSource[];
}

// Mock data
const mockProductData: ProductAnalysisData = {
  breadcrumb: {
    items: [
      { label: 'Home', href: '/bdportal/product-wiki' },
      { label: 'Acetaminophen', href: '/bdportal/product-wiki/molecule-search/acetaminophen' },
      { label: '500mg', href: '/bdportal/product-wiki/molecule-search/acetaminophen' },
      { label: 'Tablet', isActive: true },
    ],
  },
  overview: {
    tiles: [
      { value: '$18.25M', label: 'Gov TAM $', change: '+7.2%', changeType: 'positive' },
      { value: '12.5M', label: 'Gov Volume (Units)', change: '+5.9%', changeType: 'positive' },
      { value: '22%', label: 'Addressable Share', change: 'Est.', changeType: 'neutral' },
      {
        value: '$18.40-19.10',
        label: 'Est. Price Band $/Unit',
        change: 'WAC',
        changeType: 'neutral',
      },
      { value: '68', label: 'Time-to-Entry Risk', change: 'Moderate', changeType: 'neutral' },
    ],
    loeStatus: {
      status: 'Post-LOE (Year 3)',
      description: 'Generic Stable Market',
      badgeType: 'post-loe',
    },
  },
  sparklines: {
    salesTrend: [16.8, 17.2, 17.5, 17.8, 18.1, 18.25],
    volumeTrend: [11.8, 12.0, 12.2, 12.3, 12.4, 12.5],
    salesChange: '+7.2% YoY',
    volumeChange: '+5.9% YoY',
  },
  marketLandscape: {
    marketShare: [
      { name: 'Teva', percentage: 41.3, color: '#3498db' },
      { name: 'Mylan', percentage: 18.7, color: '#2ecc71' },
      { name: 'Amneal', percentage: 12.4, color: '#f39c12' },
      { name: 'Sandoz', percentage: 9.1, color: '#e74c3c' },
      { name: 'Others', percentage: 18.5, color: '#95a5a6' },
    ],
    demandDynamics: {
      pricePoints: [15.5, 16.25, 17.0, 17.75, 18.5, 19.25, 20.0, 20.75, 21.5],
      volumePoints: [22.5, 20.2, 18.8, 17.1, 15.5, 13.2, 11.8, 10.1, 8.7],
      currentPrice: 18.5,
      currentVolume: 15.5,
      elasticity: '-0.62',
      seasonality: {
        q1: 95,
        q2: 98,
        q3: 87,
        q4: 120,
      },
    },
    metrics: {
      hhiScore: 2675,
      activeLabelers: 9,
      newEntrants: 2,
    },
  },
  narrative: [
    {
      title: 'Market Opportunity Insights',
      items: [
        'Government market growing at 6.2% CAGR ($16.8M → $18.25M LTM); high concentration with top 3 holding 72.4%',
        'New entrant pace slowing (2 in last 12M vs 4 previous period) - reduced competitive pressure',
        'Moderate price elasticity (-0.62) suggests pricing power available; inelastic demand in government channel',
        'Strong Q4 seasonality (120 index) driven by federal procurement cycles and budget year-end spending',
        'Commercial market shows 12.4% higher ASP vs government ($21.30 vs $18.95) with lower volume (8.3M units)',
      ],
    },
  ],
  pricing: {
    currentBand: {
      median: 18.7,
      q1: 17.95,
      q3: 19.25,
      spread: 12.4,
    },
    trend: [
      {
        period: 'Q4 2024',
        teva: 18.95,
        mylan: 18.45,
        amneal: 17.85,
        sandoz: 18.75,
        aurobindo: 17.25,
        average: 18.25,
      },
      {
        period: 'Q3 2024',
        teva: 19.15,
        mylan: 18.65,
        amneal: 18.05,
        sandoz: 18.95,
        aurobindo: 17.45,
        average: 18.45,
      },
      {
        period: 'Q2 2024',
        teva: 19.35,
        mylan: 18.85,
        amneal: 18.25,
        sandoz: 19.15,
        aurobindo: 17.65,
        average: 18.65,
      },
      {
        period: 'Q1 2024',
        teva: 19.55,
        mylan: 19.05,
        amneal: 18.45,
        sandoz: 19.35,
        aurobindo: 17.85,
        average: 18.85,
      },
      {
        period: 'Q4 2023',
        teva: 19.75,
        mylan: 19.25,
        amneal: 18.65,
        sandoz: 19.55,
        aurobindo: 18.05,
        average: 19.05,
      },
      {
        period: 'Q3 2023',
        teva: 19.95,
        mylan: 19.45,
        amneal: 18.85,
        sandoz: 19.75,
        aurobindo: 18.25,
        average: 19.25,
      },
    ],
  },
  priceEngine: {
    suggestedWac: '$18.40-19.10',
    expectedGovNet: '$16.10',
    grossMargin: '32%',
    inputs: {
      targetMargin: 35,
      cogsEstimate: 12.5,
      discountRate: 15,
    },
    scenarios: [
      {
        type: 'Aggressive',
        wac: '$18.40',
        govNet: '$15.64',
        margin: '34.2%',
        description: 'Undercuts Q1 by 2.8%',
      },
      {
        type: 'Market',
        wac: '$18.75',
        govNet: '$15.94',
        margin: '31.8%',
        description: 'At median pricing',
      },
      {
        type: 'Premium',
        wac: '$19.10',
        govNet: '$16.24',
        margin: '29.4%',
        description: 'Above median, risk volume loss',
      },
    ],
  },
  competitors: {
    manufacturers: [
      {
        name: 'Teva Pharmaceuticals',
        labeler: 'Teva USA',
        ndcCount: 3,
        ranking: 1,
        averageWac: 18.95,
        entryDate: '2019-03',
      },
      {
        name: 'Sandoz Inc.',
        labeler: 'Sandoz',
        ndcCount: 1,
        ranking: 2,
        averageWac: 18.75,
        entryDate: '2018-11',
      },
      {
        name: 'Mylan Inc. (Viatris)',
        labeler: 'Mylan Pharmaceuticals',
        ndcCount: 2,
        ranking: 3,
        averageWac: 18.45,
        entryDate: '2020-08',
      },
      {
        name: 'Amneal Pharmaceuticals',
        labeler: 'Amneal LLC',
        ndcCount: 2,
        ranking: 4,
        averageWac: 17.85,
        entryDate: '2021-05',
      },
      {
        name: 'Cipla USA',
        labeler: 'Cipla',
        ndcCount: 1,
        ranking: 5,
        averageWac: 17.65,
        entryDate: '2023-03',
      },
      {
        name: 'Aurobindo Pharma',
        labeler: 'Aurobindo USA',
        ndcCount: 1,
        ranking: 6,
        averageWac: 17.25,
        entryDate: '2022-01',
      },
    ],
    barriers: [
      { metric: '14', value: '14', label: '# ANDAs Approved' },
      { metric: '9', value: '9', label: 'Active Labelers' },
      { metric: '5', value: '5', label: 'Dormant ANDAs' },
      { metric: '28.3', value: '28.3', label: 'Avg Time (Months)' },
    ],
  },
  anda: (() => {
    const toApp = (s: string, an: string, app: string, ad: string, _me: string): ANDAApplication => ({
      marketStatus: s,
      activeIngredient: 'Acetaminophen',
      proprietaryName: '',
      applicationNumber: an,
      dosageForm: 'Tablet',
      route: 'Oral',
      strength: '500MG',
      rld: '',
      rs: '',
      applicantHolder: app,
      effectiveDate: ad,
      marketingStatus: s,
      lastSubmissionType: '',
      lastSubmissionDate: '',
      andaLink: '',
      history: [],
    });
    const applications: ANDAApplication[] = [
      toApp('active', 'A214567', 'Teva Pharmaceuticals USA', '2019-01-15', '2019-03-01'),
      toApp('active', 'A205913', 'Mylan Pharmaceuticals', '2020-06-22', '2020-08-01'),
      toApp('active', 'A205888', 'Amneal Pharmaceuticals', '2021-03-10', '2021-05-01'),
      toApp('active', 'A205927', 'Sandoz Inc', '2018-09-05', '2018-11-01'),
      toApp('active', 'A206021', 'Aurobindo Pharma USA', '2021-11-18', '2022-01-01'),
      toApp('active', 'A205897', 'Cipla USA Inc', '2023-01-12', '2023-03-01'),
      toApp('dormant', 'A213315', "Dr. Reddy's Laboratories", '2020-06-15', 'Never'),
      toApp('dormant', 'A205790', 'Lupin Pharmaceuticals', '2021-08-25', 'Never'),
    ];
    const pagination: PaginationMetadata = {
      total_count: 8,
      page: 1,
      page_size: 10,
      total_pages: 1,
    };
    return {
      applications,
      timeline: [
        { year: 2019, name: 'Teva', color: '#3498db' },
        { year: 2020, name: 'Mylan', color: '#2ecc71' },
        { year: 2021, name: 'Amneal', color: '#f39c12' },
        { year: 2022, name: 'Aurobindo', color: '#e74c3c' },
        { year: 2023, name: 'Cipla', color: '#9b59b6' },
      ],
      pagination,
    };
  })(),
  regulatory: {
    countryMatrix: [
      {
        id: 1,
        manufacturer: 'Teva',
        apiSource: '🇺🇸 Sellersville, PA',
        fdfManufacturing: '🇺🇸 Sellersville, PA',
        taaStatus: true,
        volumeShare: '41.3%',
        fss: 'Yes',
        dapa: 'No',
        riskLevel: 'low',
      },
      {
        id: 2,
        manufacturer: 'Mylan',
        apiSource: '🇺🇸 Morgantown, WV',
        fdfManufacturing: '🇺🇸 Morgantown, WV',
        taaStatus: true,
        volumeShare: '18.7%',
        fss: 'Yes',
        dapa: 'No',
        riskLevel: 'low',
      },
      {
        id: 3,
        manufacturer: 'Amneal',
        apiSource: '🇮🇳 Ahmedabad, India',
        fdfManufacturing: '🇮🇳 Ahmedabad, India',
        taaStatus: false,
        volumeShare: '12.4%',
        fss: 'No',
        dapa: 'Yes',
        riskLevel: 'medium',
      },
      {
        id: 4,
        manufacturer: 'Sandoz',
        apiSource: '🇺🇸 Princeton, NJ',
        fdfManufacturing: '🇺🇸 Princeton, NJ',
        taaStatus: true,
        volumeShare: '9.1%',
        fss: 'Yes',
        dapa: 'No',
        riskLevel: 'low',
      },
      {
        id: 5,
        manufacturer: 'Aurobindo',
        apiSource: '🇮🇳 Hyderabad, India',
        fdfManufacturing: '🇮🇳 Hyderabad, India',
        taaStatus: false,
        volumeShare: '7.8%',
        fss: 'No',
        dapa: 'Yes',
        riskLevel: 'high',
      },
      {
        id: 6,
        manufacturer: 'Cipla',
        apiSource: '🇮🇳 Mumbai, India',
        fdfManufacturing: '🇮🇳 Mumbai, India',
        taaStatus: false,
        volumeShare: '5.2%',
        fss: 'No',
        dapa: 'No',
        riskLevel: 'medium',
      },
    ],
    taaSummary: {
      taaPercentage: 69.1,
      taaCountry: '🇺🇸 Sellersville, PA',
      nonTaaPercentage: 25.4,
      nonTaaCountry: '🇮🇳 Ahmedabad, India',
      summaryText:
        '69.1% volume from TAA (🇺🇸 Sellersville, PA) | 25.4% volume from Non-TAA (🇮🇳 Ahmedabad, India)',
    },
    attributes: [
      {
        title: 'Product Labeling (SPL Extract)',
        items: [
          { label: 'Active Ingredient', value: 'Acetaminophen 500mg' },
          { label: 'Dosage Form', value: 'Tablet, film coated' },
          { label: 'NDC Pattern', value: 'XXXXX-XXX-XX (500mg, 100ct bottles standard)' },
          { label: 'Therapeutic Class', value: 'Analgesic, Antipyretic' },
        ],
      },
      {
        title: 'Regulatory Classifications',
        items: [
          { label: 'DEA Schedule', value: 'Not controlled substance' },
          { label: 'OTC Status', value: 'Available OTC and Rx' },
          { label: 'REMS', value: 'No REMS required' },
          { label: 'Biosimilar', value: 'N/A (Small molecule)' },
        ],
      },
    ],
    complianceScore: {
      score: 78,
      level: 'high',
      description: 'High TAA compliance, clean inspection history, simple manufacturing',
      factors: [
        { name: 'TAA Compliance (40%)', weight: '8.5/10', score: '73% TAA volume', details: '' },
        {
          name: 'Inspection History (25%)',
          weight: '9.2/10',
          score: 'No recent 483s for major players',
          details: '',
        },
        {
          name: 'Recall History (20%)',
          weight: '7.8/10',
          score: 'Amneal recall 2023, resolved',
          details: '',
        },
        {
          name: 'Warning Letters (10%)',
          weight: '8.5/10',
          score: 'Aurobindo WL 2022, addressed',
          details: '',
        },
        {
          name: 'REMS/Complex (5%)',
          weight: '10/10',
          score: 'Simple oral solid dosage form',
          details: '',
        },
      ],
    },
  },
  compliance: {},
  dataSources: [
    { id: 1, source_name: 'DailyMed SPL data', updated_at: new Date().toISOString() },
    { id: 2, source_name: 'FDA Orange Book', updated_at: new Date().toISOString() },
    { id: 3, source_name: 'FDA inspection records', updated_at: new Date().toISOString() },
    { id: 4, source_name: 'Market research databases', updated_at: new Date().toISOString() },
    { id: 5, source_name: 'Government procurement data', updated_at: new Date().toISOString() },
    { id: 6, source_name: 'Competitor intelligence', updated_at: new Date().toISOString() },
    { id: 7, source_name: 'Regulatory filings', updated_at: new Date().toISOString() },
    { id: 8, source_name: 'Supply chain analytics', updated_at: new Date().toISOString() },
  ],
};

export const useProductAnalysisLogic = () => {
  const [searchParams] = useSearchParams();
  const [productData, setProductData] = useState<ProductAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoadRef = useRef(true);
  const previousGfcRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchProductData = async () => {
      let shouldSetLoadingFalse = false;
      try {
        // Get GFC and molecule/concept info from URL params
        const gfc = searchParams.get('gfc');
        // Get the active tab from URL params - determines which data to fetch initially
        const activeTab = searchParams.get('tab') || 'overview';

        // Reset initial load flag if GFC changes (new product)
        if (gfc !== previousGfcRef.current && previousGfcRef.current !== null) {
          isInitialLoadRef.current = true;
        }
        previousGfcRef.current = gfc;

        // Only show main loading state on initial load, not when switching tabs
        const isInitialLoad = isInitialLoadRef.current;
        if (isInitialLoad) {
          setLoading(true);
          shouldSetLoadingFalse = true;
          isInitialLoadRef.current = false;
        }

        // Fetch Data Sources from API (doesn't depend on GFC, always fetch)
        let dataSources: DataSource[] = [];
        try {
          const dataSourcesResponse = await productWikiServices.getDataSources();
          if (dataSourcesResponse.data && Array.isArray(dataSourcesResponse.data)) {
            dataSources = dataSourcesResponse.data;
          }
        } catch (_apiError) {
          toast.error('Error fetching data sources');
          // Use empty array if API call fails - no mock data fallback
        }
        const item = searchParams.get('item'); // Molecule/concept name
        const type = searchParams.get('type'); // 'molecule', 'concept', or 'ndc'
        const label = searchParams.get('label'); // Display label
        const strength = searchParams.get('strength'); // Strength value
        const dosageForms = searchParams.get('dosageForms'); // Dosage forms

        // Validate GFC - check if it exists and is not empty
        if (!gfc || gfc.trim() === '') {
          // If no GFC, use mock data for other sections but API data sources
          const updatedData = { ...mockProductData };
          updatedData.dataSources = dataSources;
          setProductData(updatedData);
          setLoading(false);
          return;
        }

        // Only fetch Opportunity data on initial load if the active tab is 'overview'
        // Other tabs will fetch their own data via the tab-based lazy loading in ProductAnalysis
        // This prevents unnecessary API calls when user lands directly on another tab
        if (activeTab === 'overview') {
          try {
            await productAnalysisStore.fetchAllOpportunityData({
              gfc: gfc.trim(),
              period: '3 months',
            });
          } catch (_apiError) {
            toast.error('Error fetching opportunity at a glance data');
            // Continue to check store state even if API call fails
          }

          // Wait a bit for store to update
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Combine API data with mock data
        const updatedData = { ...mockProductData };

        // Update opportunity data if available
        if (productAnalysisStore.opportunityOverview && productAnalysisStore.sparklineData) {
          updatedData.overview = productAnalysisStore.opportunityOverview;
          updatedData.sparklines = productAnalysisStore.sparklineData;
        }

        // Update competitors data if available
        if (productAnalysisStore.competitorsData) {
          updatedData.competitors = productAnalysisStore.competitorsData.competitors;
          updatedData.anda = productAnalysisStore.competitorsData.anda;
        }

        // Update regulatory data if available
        if (productAnalysisStore.regulatoryData) {
          // Map API response to RegulatoryData interface
          updatedData.regulatory = {
            countryMatrix: productAnalysisStore.regulatoryData.countryMatrix.map(item => ({
              id: item.id,
              manufacturer: item.manufacturer,
              apiSource: item.apiSource,
              fdfManufacturing: item.fdfManufacturing,
              taaStatus: item.taaStatus,
              volumeShare: item.volumeShare,
              fss: item.fss,
              dapa: item.dapa,
              riskLevel: item.riskLevel,
            })),
            taaSummary: productAnalysisStore.regulatoryData.taaSummary || {
              taaPercentage: 0,
              taaCountry: '',
              nonTaaPercentage: 0,
              nonTaaCountry: '',
              summaryText: 'No data available',
            },
            attributes: productAnalysisStore.regulatoryData.attributes,
            complianceScore: productAnalysisStore.regulatoryData.complianceScore,
          };
        }

        // Update data sources with API response
        updatedData.dataSources = dataSources;

        // Update breadcrumb with actual molecule/concept data from URL params
        if (item && type) {
          const moleculeName = label || item; // Use label if available, otherwise use item
          const moleculeSearchUrl = `/bdportal/product-wiki/molecule-search?item=${encodeURIComponent(item)}&type=${type}${label ? `&label=${encodeURIComponent(label)}` : ''}`;

          // Build pack size label from strength and dosage forms
          let packSizeLabel = '';
          if (strength && dosageForms) {
            // Format: "500mg Tablet" or "325mg Tablets, Chewable Tablets"
            const firstDosageForm = dosageForms.split(',')[0].trim();
            packSizeLabel = `${strength} ${firstDosageForm}`;
          } else if (strength) {
            packSizeLabel = strength;
          } else if (dosageForms) {
            packSizeLabel = dosageForms.split(',')[0].trim();
          } else if (gfc) {
            // Fallback to GFC if no strength/dosage form
            packSizeLabel = gfc;
          } else {
            packSizeLabel = 'Product Analysis';
          }

          // Build breadcrumb: Home > Molecule Name > Pack Size (Strength + Dosage Form)
          updatedData.breadcrumb = {
            items: [
              { label: 'Home', href: '/bdportal/product-wiki' },
              {
                label: moleculeName,
                href: moleculeSearchUrl,
              },
              {
                label: packSizeLabel,
                isActive: true,
              },
            ],
          };
        } else if (gfc) {
          // If no item/type but we have GFC, use label (concept name) if available, otherwise GFC
          updatedData.breadcrumb = {
            items: [
              { label: 'Home', href: '/bdportal/product-wiki' },
              {
                label: label || gfc,
                isActive: true,
              },
            ],
          };
        }

        // Only check for opportunity error on initial load
        // Other tab errors will be handled by their respective tabs
        if (productAnalysisStore.opportunityError) {
          setError(productAnalysisStore.opportunityError);
        }

        setProductData(updatedData);
      } catch (_err) {
        setError('Failed to load product analysis data');

        // Even on error, try to fetch data sources from API
        let dataSources: DataSource[] = [];
        try {
          const dataSourcesResponse = await productWikiServices.getDataSources();
          if (dataSourcesResponse.data && Array.isArray(dataSourcesResponse.data)) {
            dataSources = dataSourcesResponse.data;
          }
        } catch (_apiError) {
          toast.error('Error fetching data sources');
        }

        // Use mock data for other sections but API data sources
        const errorData = { ...mockProductData };
        errorData.dataSources = dataSources;
        setProductData(errorData);
      } finally {
        // Only set loading to false if we set it to true (i.e., on initial load)
        // On tab switches, loading stays false and individual tabs handle their own loading states
        if (shouldSetLoadingFalse) {
          setLoading(false);
        }
      }
    };

    fetchProductData();
    // Only re-fetch when GFC-related params change, not when tab changes
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    searchParams.get('gfc'),
    searchParams.get('item'),
    searchParams.get('type'),
    searchParams.get('label'),
    searchParams.get('strength'),
    searchParams.get('dosageForms'),
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Return loading state from store for Opportunity section
  const opportunityLoading = productAnalysisStore.isOpportunityLoading;

  return {
    productData,
    loading,
    error,
    opportunityLoading,
  };
};
